/**
 * Memory & Tools tab — read-only display of the Tier-B-org memory +
 * tools blocks. Surfaces what the org has configured (citations
 * memory, tool allow/deny overlays) so an ORG_ADMIN viewing in LexIDE
 * can confirm the runtime's effective state without bouncing to
 * LexPortal. Edits route to the canonical org-rail editor at
 * `/org/runtime-config` (same pattern as the Agent tab).
 *
 * The Tier-A floor (raw fs/exec/canvas/gateway deny, browser invariants,
 * etc.) is operator-only and NOT editable through any UI — surfaced
 * here with a "locked by platform" badge so the ORG_ADMIN understands
 * why those tools never appear in the allow surface.
 */

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Brain, Building2, ExternalLink, Lock } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useOpenclawSettings } from "./OpenclawSettingsTabs";

const TIER_A_DENY_GROUPS = [
  "fs.*",
  "exec.*",
  "canvas.*",
  "gateway.*",
];

export function MemoryToolsTab() {
  const { t } = useTranslation();
  const { config } = useOpenclawSettings();
  const navigate = useNavigate();
  const { identity } = useAuthIdentity();
  const isOrgAdmin = identity?.isOrgAdmin ?? false;

  const memory = config.org.memory ?? {};
  const tools = config.org.tools ?? {};

  return (
    <Card variant="surface" data-testid="openclaw-memory-tab">
      <div className="flex items-center gap-2">
        <Brain size={18} className="text-cream-500" />
        <h3 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
          {t("settings.openclaw.memory.title")}
        </h3>
        <span
          title={t("settings.openclaw.agent.managedByOrgTooltip")}
          className="inline-flex items-center gap-1 rounded bg-cream-100 px-1.5 py-0.5 text-[10px] font-medium text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
        >
          <Lock size={9} />
          {t("settings.openclaw.agent.managedByOrg")}
        </span>
      </div>
      <p className="mt-3 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("settings.openclaw.memory.caption")}
      </p>

      <div className="mt-5 flex flex-col gap-5 text-body-sm">
        {/* Memory block — open shape per the API client. We display
            top-level keys with their JSON values so the operator sees
            EXACTLY what the runtime reads, even if the schema grows. */}
        <section>
          <p className="mb-2 text-caption font-medium uppercase tracking-wide text-coffee-500 dark:text-cream-500">
            {t("settings.openclaw.memory.memoryHeading")}
          </p>
          {Object.keys(memory).length === 0 ? (
            <p className="italic text-coffee-500 dark:text-cream-500">
              {t("settings.openclaw.memory.memoryEmpty")}
            </p>
          ) : (
            <pre className="overflow-x-auto rounded-md border border-cream-200/60 bg-cream-50 p-3 font-mono text-xs text-coffee-800 dark:border-coffee-700/60 dark:bg-coffee-900/60 dark:text-cream-200">
              {JSON.stringify(memory, null, 2)}
            </pre>
          )}
        </section>

        {/* Tools block — Tier-B-org `tools.alsoAllow` / `additionalDeny`.
            The Tier-A floor (raw fs/exec/canvas/gateway) is enforced at
            the OpenClaw policy layer and is operator-only; we surface
            it as a fixed locked panel so the ORG_ADMIN doesn't wonder
            why they can't add `exec.run` to alsoAllow. */}
        <section>
          <p className="mb-2 text-caption font-medium uppercase tracking-wide text-coffee-500 dark:text-cream-500">
            {t("settings.openclaw.memory.toolsHeading")}
          </p>
          {Object.keys(tools).length === 0 ? (
            <p className="italic text-coffee-500 dark:text-cream-500">
              {t("settings.openclaw.memory.toolsEmpty")}
            </p>
          ) : (
            <pre className="overflow-x-auto rounded-md border border-cream-200/60 bg-cream-50 p-3 font-mono text-xs text-coffee-800 dark:border-coffee-700/60 dark:bg-coffee-900/60 dark:text-cream-200">
              {JSON.stringify(tools, null, 2)}
            </pre>
          )}

          <div className="mt-3 rounded-md border border-cream-200/60 bg-cream-50 p-3 dark:border-coffee-700/60 dark:bg-coffee-900/60">
            <div className="mb-1 flex items-center gap-1.5">
              <Lock size={11} className="text-coffee-500 dark:text-cream-500" />
              <span className="text-caption font-medium text-coffee-700 dark:text-cream-300">
                {t("settings.openclaw.memory.tierAFloorLabel")}
              </span>
            </div>
            <p className="text-caption text-coffee-500 dark:text-cream-500">
              {t("settings.openclaw.memory.tierAFloorHint")}
            </p>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {TIER_A_DENY_GROUPS.map((g) => (
                <li
                  key={g}
                  className="inline-flex items-center rounded bg-cream-100 px-1.5 py-0.5 font-mono text-[10px] text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
                >
                  {g}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      {isOrgAdmin && (
        <div className="mt-5">
          <button
            type="button"
            data-testid="openclaw-memory-edit-on-org-rail"
            // Same fallback as AgentTab — see its comment for why.
            onClick={() => navigate("/org")}
            className="inline-flex items-center gap-1.5 rounded-md border border-cream-200/60 px-3 py-1.5 text-body-sm font-medium text-coffee-700 transition-colors hover:bg-cream-50 hover:text-coffee-900 dark:border-coffee-700/60 dark:text-cream-200 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100"
          >
            <Building2 size={13} aria-hidden />
            {t("settings.openclaw.agent.editOnOrgRail")}
            <ExternalLink size={11} aria-hidden />
          </button>
        </div>
      )}
    </Card>
  );
}
